// VideoCallManager — framework-agnostic 1:1 WebRTC core for EMDRlink.
//
// UI-framework-agnostic (no React, no DOM) so it can be unit-tested headlessly
// and reused anywhere, mirroring the src/devices/DeviceManager adapter pattern
// (ADR-0013). It knows nothing about *how* signaling travels: the caller injects
// a `signaling` transport (in the app: the existing therapy Socket.IO room, via
// socketSignaling.ts) and a `media` provider (in the app: getUserMedia).
//
// Design rule from RFC-0001 §6 / ADR-0014: video is ADDITIVE. Every failure path
// is non-throwing so a broken call can never take down the BLS light-bar session.

export type VideoCallState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'failed'
  | 'declined'
  | 'closed';

export interface SignalingTransport {
  /** Send a signaling message to the peer(s) in the session room. */
  send(type: string, payload: unknown): void;
  /** Subscribe to a signaling message from the peer. Returns an unsubscribe fn. */
  on(type: string, handler: (payload: any) => void): () => void;
}

export interface MediaProvider {
  /** Resolve the local camera/mic stream. */
  getLocalStream(): Promise<MediaStream>;
}

export interface VideoCallManagerOptions {
  signaling: SignalingTransport;
  media: MediaProvider;
  /** Factory (overridable in tests). */
  createPeerConnection?: () => RTCPeerConnection;
  iceServers?: RTCIceServer[];
  onStateChange?: (state: VideoCallState) => void;
  onRemoteStream?: (stream: MediaStream) => void;
  onLocalStream?: (stream: MediaStream) => void;
  onError?: (err: Error, context: string) => void;
}

export const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
];

export class VideoCallManager {
  private signaling: SignalingTransport;
  private media: MediaProvider;
  private iceServers: RTCIceServer[];
  private onStateChange: (state: VideoCallState) => void;
  private onRemoteStream: (stream: MediaStream) => void;
  private onLocalStream: (stream: MediaStream) => void;
  private onError: (err: Error, context: string) => void;
  private createPeerConnection: () => RTCPeerConnection;
  private unsubscribes: Array<() => void> = [];

  pc: RTCPeerConnection | null = null;
  localStream: MediaStream | null = null;
  state: VideoCallState = 'idle';

  constructor(options: VideoCallManagerOptions) {
    if (!options || !options.signaling || !options.media) {
      throw new Error('VideoCallManager requires { signaling, media }');
    }
    this.signaling = options.signaling;
    this.media = options.media;
    this.iceServers = options.iceServers || DEFAULT_ICE_SERVERS;
    this.onStateChange = options.onStateChange || (() => undefined);
    this.onRemoteStream = options.onRemoteStream || (() => undefined);
    this.onLocalStream = options.onLocalStream || (() => undefined);
    this.onError = options.onError || (() => undefined);
    this.createPeerConnection =
      options.createPeerConnection ||
      (() => new RTCPeerConnection({ iceServers: this.iceServers }));
    this.wireSignaling();
  }

  private setState(next: VideoCallState): void {
    this.state = next;
    try {
      this.onStateChange(next);
    } catch {
      /* observer errors must never bubble into the call */
    }
  }

  private wireSignaling(): void {
    this.unsubscribes.push(
      this.signaling.on('webrtc-offer', (p) => this.onRemoteOffer(p)),
      this.signaling.on('webrtc-answer', (p) => this.onRemoteAnswer(p)),
      this.signaling.on('webrtc-ice-candidate', (p) =>
        this.onRemoteIceCandidate(p),
      ),
      this.signaling.on('webrtc-hangup', () => this.onRemoteHangup()),
    );
  }

  private async ensureLocalMedia(): Promise<MediaStream | null> {
    if (this.localStream) return this.localStream;
    try {
      this.localStream = await this.media.getLocalStream();
      this.onLocalStream(this.localStream);
      return this.localStream;
    } catch (err) {
      // Permission denied / no device — a declined call must not throw upward.
      this.setState('declined');
      this.onError(err as Error, 'getLocalStream');
      return null;
    }
  }

  private createPc(): RTCPeerConnection {
    const pc = this.createPeerConnection();

    pc.onicecandidate = (event) => {
      if (event && event.candidate) {
        this.signaling.send('webrtc-ice-candidate', {
          candidate: event.candidate,
        });
      }
    };
    pc.ontrack = (event) => {
      const stream = (event.streams && event.streams[0]) || null;
      if (stream) this.onRemoteStream(stream);
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === 'connected') this.setState('connected');
      else if (s === 'failed' || s === 'disconnected') this.setState('failed');
    };

    if (this.localStream) {
      for (const track of this.localStream.getTracks()) {
        pc.addTrack(track, this.localStream);
      }
    }
    this.pc = pc;
    return pc;
  }

  /** Caller side: acquire media, create the peer connection, send an offer. */
  async startCall(): Promise<void> {
    this.setState('connecting');
    const stream = await this.ensureLocalMedia();
    if (!stream) return; // declined — stay in 'declined', never throw
    try {
      const pc = this.createPc();
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.signaling.send('webrtc-offer', { sdp: pc.localDescription });
    } catch (err) {
      this.setState('failed');
      this.onError(err as Error, 'startCall');
    }
  }

  /**
   * Callee side: be ready to answer an inbound offer. Signaling is already
   * wired in the constructor, and media is acquired lazily in onRemoteOffer
   * when an offer actually arrives — so the callee's camera stays OFF until the
   * initiator actually starts video. This is what makes "video is optional per
   * session" real for the patient: if the therapist never starts a call, the
   * patient is never prompted for camera/mic.
   */
  async acceptCalls(): Promise<void> {
    // Intentionally a no-op beyond documenting intent; kept for API symmetry
    // with startCall() and so callers have an explicit "I am the callee" step.
  }

  private async onRemoteOffer(payload: {
    sdp: RTCSessionDescriptionInit;
  }): Promise<void> {
    try {
      if (this.state === 'idle' || this.state === 'declined') {
        this.setState('connecting');
      }
      const stream = await this.ensureLocalMedia();
      if (!stream) return;
      const pc = this.pc || this.createPc();
      await pc.setRemoteDescription(payload.sdp);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.signaling.send('webrtc-answer', { sdp: pc.localDescription });
    } catch (err) {
      this.setState('failed');
      this.onError(err as Error, 'onRemoteOffer');
    }
  }

  private async onRemoteAnswer(payload: {
    sdp: RTCSessionDescriptionInit;
  }): Promise<void> {
    try {
      if (!this.pc) return;
      await this.pc.setRemoteDescription(payload.sdp);
    } catch (err) {
      this.setState('failed');
      this.onError(err as Error, 'onRemoteAnswer');
    }
  }

  private async onRemoteIceCandidate(payload: {
    candidate: RTCIceCandidateInit;
  }): Promise<void> {
    try {
      if (!this.pc || !payload || !payload.candidate) return;
      await this.pc.addIceCandidate(payload.candidate);
    } catch (err) {
      // A single bad candidate should not fail the call.
      this.onError(err as Error, 'onRemoteIceCandidate');
    }
  }

  /**
   * The peer ended the call (e.g. the therapist toggled video off). Close our
   * side and release the camera/mic, but KEEP listening on signaling so a later
   * offer (the therapist re-starting video) reconnects us. Back to 'idle' so
   * the UI hides the panel — this is what makes "disable video" propagate to
   * the patient (their camera light goes out, their tile disappears).
   */
  private onRemoteHangup(): void {
    this.resetCall();
  }

  /**
   * Soft-reset to a ready-to-reconnect state: close the peer connection and
   * release local media, but keep signaling wired. Used both when the peer
   * hangs up and when the peer disconnects (closed tab/browser) — in either
   * case we must drop the now-dead remote video instead of showing a frozen
   * frame, while staying ready to reconnect. Non-throwing.
   */
  resetCall(): void {
    this.closeConnection();
    this.releaseLocalMedia();
    this.setState('idle');
  }

  private closeConnection(): void {
    try {
      if (this.pc) {
        this.pc.onicecandidate = null;
        this.pc.ontrack = null;
        this.pc.onconnectionstatechange = null;
        this.pc.close();
      }
    } catch {
      /* ignore */
    }
    this.pc = null;
  }

  private releaseLocalMedia(): void {
    if (this.localStream) {
      for (const track of this.localStream.getTracks()) {
        try {
          track.stop();
        } catch {
          /* ignore */
        }
      }
    }
    this.localStream = null;
  }

  /**
   * Pause or resume the OUTBOUND video track without renegotiating.
   * Used to keep the camera device open (so the peer's tile doesn't freeze
   * hard) while temporarily muting video. NOTE: EMDRlink does NOT pause the
   * patient's outbound video during BLS sets — the therapist must keep
   * observing the client — this is a general-purpose control.
   */
  setVideoEnabled(enabled: boolean): void {
    if (!this.localStream) return;
    for (const track of this.localStream.getVideoTracks()) {
      track.enabled = enabled;
    }
  }

  /**
   * Tear the call down. Idempotent and non-throwing. By default it also tells
   * the peer to hang up (so ending video on one side ends it on the other);
   * pass notifyPeer=false when responding to a peer-initiated hangup.
   */
  hangUp(notifyPeer = true): void {
    if (notifyPeer) {
      try {
        this.signaling.send('webrtc-hangup', {});
      } catch {
        /* ignore — teardown must not throw */
      }
    }
    for (const unsub of this.unsubscribes) {
      try {
        unsub();
      } catch {
        /* ignore */
      }
    }
    this.unsubscribes = [];
    this.closeConnection();
    this.releaseLocalMedia();
    this.setState('closed');
  }
}
